import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "submitButton",
    // Practice fields
    "practiceName",
    "phoneNumber",
    // Practice type (#3016) — General Dentist or Specialist
    "practiceTypeSection",
    "practiceTypeInput",
    // Hidden General Dentistry specialty inputs, enabled for a general dentist
    // practice (the specialty selects don't offer General Dentistry)
    "generalDentistryProviderInput",
    "generalDentistryInviteInput",
    // Address fields (lookup mode)
    "addressLookupSection",
    "addressLookup",
    "googleStreet",  // Hidden field populated by Google Places
    // Address fields (manual mode)
    "manualAddressSection",
    "streetAddress",
    "street2",
    "city",
    "state",
    "zipCode",
    // Role selection
    "roleProvider",
    "roleAdmin",
    "roleProviderInput",
    "roleAdminInput",
    // User name fields (shown for both roles)
    "nameFieldsSection",
    "firstName",
    "lastName",
    // Provider specialty field (when user is provider)
    "providerSpecialtySection",
    "providerSpecialty",
    // Add-your-provider fields (when user is admin)
    "inviteProviderSection",
    "inviteProviderFirstName",
    "inviteProviderLastName",
    "inviteProviderSpecialty",
    "inviteProviderEmail",
    // Join-existing-organization banner (verified email domain match)
    "joinBanner",
    "joinPrompt",
    "joinedConfirmation",
    "joinOrganizationCode",
    "practiceFieldsSection"
  ]

  connect() {
    this.isManualAddress = false
    this.addressSelectedFromGoogle = false

    // Restore the join-organization choice from the hidden field (survives the
    // server re-render after a validation error, where the practice fields are
    // already rendered filled and disabled).
    this.joinedOrganization = this.hasJoinOrganizationCodeTarget && !!this.joinOrganizationCodeTarget.value

    // Restore the selected role from the rendered radios (handles server re-render
    // after a validation error, where a role may already be checked).
    if (this.hasRoleProviderInputTarget && this.roleProviderInputTarget.checked) {
      this.selectedRole = "provider"
    } else if (this.hasRoleAdminInputTarget && this.roleAdminInputTarget.checked) {
      this.selectedRole = "administrator"
    } else {
      this.selectedRole = null
    }
    // A server re-render after a validation error keeps the role; the name
    // fields must still match it (an administrator never shows the
    // referring provider's name).
    this.applyEmbeddedProviderPrefill(this.selectedRole)

    this.updateFieldsVisibility()
    this.applyPracticeType()
    this.updateAddressFieldsDisabledState()
  }

  // Toggle between address lookup and manual entry
  toggleAddressMode(event) {
    event.preventDefault()
    this.isManualAddress = !this.isManualAddress

    if (this.isManualAddress) {
      this.addressSelectedFromGoogle = false
    }

    if (this.hasAddressLookupSectionTarget && this.hasManualAddressSectionTarget) {
      this.addressLookupSectionTarget.classList.toggle("d-none", this.isManualAddress)
      this.manualAddressSectionTarget.classList.toggle("d-none", !this.isManualAddress)
    }

    this.clearAddressError()
    this.updateAddressFieldsDisabledState()
  }

  // Disable address fields that aren't being used to prevent them from being submitted
  updateAddressFieldsDisabledState() {
    const disableManual = !this.isManualAddress
    if (this.hasStreetAddressTarget) this.streetAddressTarget.disabled = disableManual
    if (this.hasStreet2Target) this.street2Target.disabled = disableManual
    if (this.hasCityTarget) this.cityTarget.disabled = disableManual
    if (this.hasStateTarget) this.stateTarget.disabled = disableManual
    if (this.hasZipCodeTarget) this.zipCodeTarget.disabled = disableManual

    const disableGoogle = this.isManualAddress
    if (this.hasGoogleStreetTarget) this.googleStreetTarget.disabled = disableGoogle
    if (this.hasAddressLookupSectionTarget) {
      const hiddenFields = this.addressLookupSectionTarget.querySelectorAll('input[type="hidden"]')
      hiddenFields.forEach(field => field.disabled = disableGoogle)
    }
  }

  // Called when an address is selected from Google Places
  onAddressSelected() {
    this.addressSelectedFromGoogle = true
    this.clearAddressError()
  }

  // --- Join existing organization (verified email domain match) -----------

  // Hide the practice section — the joined organization's details are already
  // set up, so the member only completes the "Tell us about yourself" section.
  joinOrganization(event) {
    event.preventDefault()
    this.joinedOrganization = true

    this.joinOrganizationCodeTarget.value = this.joinBannerTarget.dataset.orgCode

    if (this.hasPracticeFieldsSectionTarget) this.practiceFieldsSectionTarget.classList.add("d-none")

    // Full orgs get the amber "team is full" confirmation variant
    if (this.joinBannerTarget.dataset.orgFull === "true") {
      this.joinBannerTarget.classList.add("join-org-banner--full")
    }

    this.joinPromptTarget.classList.add("d-none")
    this.joinedConfirmationTarget.classList.remove("d-none")
    this.updateFieldsVisibility()
    this.applyPracticeType()
  }

  // Show the practice section again so the user can set up their own organization
  leaveOrganization(event) {
    event.preventDefault()
    this.joinedOrganization = false

    this.joinOrganizationCodeTarget.value = ""
    this.joinBannerTarget.classList.remove("join-org-banner--full")

    if (this.hasPracticeFieldsSectionTarget) this.practiceFieldsSectionTarget.classList.remove("d-none")

    this.joinedConfirmationTarget.classList.add("d-none")
    this.joinPromptTarget.classList.remove("d-none")
    this.updateFieldsVisibility()
    this.applyPracticeType()
  }

  // Handle role selection
  selectRole(event) {
    const role = event.currentTarget.dataset.role
    this.selectedRole = role

    if (this.hasRoleProviderTarget && this.hasRoleAdminTarget) {
      this.roleProviderTarget.classList.toggle("selected", role === "provider")
      this.roleAdminTarget.classList.toggle("selected", role === "administrator")
    }

    // Clear the inputs that belong to the role being switched away from, so the
    // wrong role's data is never submitted (shared name fields are kept).
    this.clearRoleFields(role)
    this.applyEmbeddedProviderPrefill(role)

    this.updateFieldsVisibility()
    this.applyPracticeType()
    this.clearRoleError()
  }

  // An embedded-form submitter arrives with the referring provider's name and
  // email (data-prefill-* on the name and "Add your Provider" sections). That
  // person is the user only when they sign up as the provider: fill empty
  // name fields for a provider; for an administrator signing up on their
  // behalf, clear name fields still holding that name and offer the provider
  // in "Add your Provider" instead. Anything the user typed is left alone.
  applyEmbeddedProviderPrefill(role) {
    const nameSection = this.optionalTarget("nameFieldsSection")
    if (role === "provider") {
      this.fillIfEmpty(nameSection, [["firstName", "prefillFirstName"], ["lastName", "prefillLastName"]])
    } else if (role === "administrator") {
      if (nameSection) {
        const { prefillFirstName = "", prefillLastName = "" } = nameSection.dataset
        ;[["firstName", prefillFirstName], ["lastName", prefillLastName]].forEach(([name, prefill]) => {
          const field = this.optionalTarget(name)
          if (field && prefill && field.value.trim() === prefill) {
            field.value = ""
            this.clearFieldError(field)
          }
        })
      }
      this.fillIfEmpty(this.optionalTarget("inviteProviderSection"), [
        ["inviteProviderFirstName", "prefillFirstName"],
        ["inviteProviderLastName", "prefillLastName"],
        ["inviteProviderEmail", "prefillEmail"]
      ])
    }
  }

  // Fills each empty target from the section's data-prefill-* attribute.
  fillIfEmpty(section, pairs) {
    if (!section) return

    pairs.forEach(([name, key]) => {
      const field = this.optionalTarget(name)
      const prefill = section.dataset[key] || ""
      if (field && prefill && !field.value.trim()) {
        field.value = prefill
        this.clearFieldError(field)
      }
    })
  }

  // this.xTarget throws when the target is absent; null instead.
  optionalTarget(name) {
    const capitalized = name.charAt(0).toUpperCase() + name.slice(1)
    return this[`has${capitalized}Target`] ? this[`${name}Target`] : null
  }

  // Clear the now-hidden role section's fields when the role changes.
  clearRoleFields(role) {
    if (role === "administrator") {
      // Switched to admin → the provider specialty section is now hidden
      if (this.hasProviderSpecialtyTarget) this.clearSelect(this.providerSpecialtyTarget)
    } else if (role === "provider") {
      // Switched to provider → the "Add your Provider" section is now hidden
      ;[
        this.hasInviteProviderFirstNameTarget && this.inviteProviderFirstNameTarget,
        this.hasInviteProviderLastNameTarget && this.inviteProviderLastNameTarget,
        this.hasInviteProviderEmailTarget && this.inviteProviderEmailTarget
      ].forEach((field) => {
        if (field) {
          field.value = ""
          this.clearFieldError(field)
        }
      })
      if (this.hasInviteProviderSpecialtyTarget) this.clearSelect(this.inviteProviderSpecialtyTarget)
    }
  }

  // Reset a (possibly Select2-enhanced) multi-select and clear its error state.
  clearSelect(select) {
    select.querySelectorAll("option").forEach((opt) => { opt.selected = false })
    // Select2 binds via jQuery; trigger through it when present so the chips clear.
    if (window.jQuery && window.jQuery(select).data("select2")) {
      window.jQuery(select).val(null).trigger("change")
    } else {
      select.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.clearFieldError(select)
  }

  updateFieldsVisibility() {
    const isProvider = this.selectedRole === "provider"
    const isAdmin = this.selectedRole === "administrator"
    const hasRole = isProvider || isAdmin

    if (this.hasNameFieldsSectionTarget) {
      this.nameFieldsSectionTarget.classList.toggle("d-none", !hasRole)
    }
    if (this.hasPracticeTypeSectionTarget) {
      this.practiceTypeSectionTarget.classList.toggle("d-none", !hasRole || this.joinedOrganization)
    }
    // The specialty is only asked of a specialist. A general dentist's is
    // General Dentistry, which applyPracticeType submits through a hidden input.
    const isSpecialist = this.practiceType === "specialist"
    if (this.hasProviderSpecialtySectionTarget) {
      this.providerSpecialtySectionTarget.classList.toggle("d-none", !isProvider || !isSpecialist)
    }
    if (this.hasInviteProviderSpecialtyTarget) {
      const group = this.inviteProviderSpecialtyTarget.closest(".custom-multi-select")
      if (group) group.classList.toggle("d-none", !isSpecialist)
    }
    if (this.hasInviteProviderSectionTarget) {
      // A joined organization already has its providers — never ask for one
      this.inviteProviderSectionTarget.classList.toggle("d-none", !isAdmin || this.joinedOrganization)
    }
  }

  // Clear a field's error as the user edits it
  onInputChange(event) {
    if (event && event.target) this.clearFieldError(event.target)
  }

  // Collect every required field that is currently empty, with a friendly message
  collectErrors() {
    const errors = []

    // The practice section is hidden while joining an existing organization
    if (!this.joinedOrganization) {
      if (this.hasPracticeNameTarget && !this.practiceNameTarget.value.trim()) {
        errors.push({ target: this.practiceNameTarget, message: "Enter your practice name" })
      }
      if (this.hasPhoneNumberTarget && !this.phoneNumberTarget.value.trim()) {
        errors.push({ target: this.phoneNumberTarget, message: "Enter your practice phone number" })
      }

      // Address — validate whichever entry mode is visible
      if (this.isManualAddress) {
        if (this.hasStreetAddressTarget && !this.streetAddressTarget.value.trim()) {
          errors.push({ target: this.streetAddressTarget, message: "Enter the street address" })
        }
        if (this.hasCityTarget && !this.cityTarget.value.trim()) {
          errors.push({ target: this.cityTarget, message: "Enter the city" })
        }
        if (this.hasStateTarget && !this.stateTarget.value.trim()) {
          errors.push({ target: this.stateTarget, message: "Enter the state" })
        }
        if (this.hasZipCodeTarget && !this.zipCodeTarget.value.trim()) {
          errors.push({ target: this.zipCodeTarget, message: "Enter the zip code" })
        }
      } else if (this.hasGoogleStreetTarget && !this.googleStreetTarget.value.trim()) {
        errors.push({ address: true, message: "Add your practice address" })
      }
    }

    // Role
    if (!this.selectedRole) {
      errors.push({ role: true, message: "Select your role" })
    }

    // Practice type (#3016), once a role is chosen. A joined organization
    // already has one.
    if (this.selectedRole && !this.joinedOrganization && this.hasPracticeTypeInputTarget &&
        !this.practiceTypeInputTargets.some((input) => input.checked)) {
      errors.push({ practiceType: true, message: "Choose General Dentist or Specialist" })
    }

    // Name (both roles)
    if (this.hasFirstNameTarget && !this.firstNameTarget.value.trim()) {
      errors.push({ target: this.firstNameTarget, message: "Enter your first name" })
    }
    if (this.hasLastNameTarget && !this.lastNameTarget.value.trim()) {
      errors.push({ target: this.lastNameTarget, message: "Enter your last name" })
    }

    // Specialty (specialist providers only; a general dentist's is submitted
    // by the hidden input)
    if (this.selectedRole === "provider" && this.practiceType === "specialist") {
      if (this.hasProviderSpecialtyTarget && this.providerSpecialtyTarget.selectedOptions.length === 0) {
        errors.push({ target: this.providerSpecialtyTarget, message: "Select your specialty" })
      }
    }

    // Add-your-provider (admins only) — all fields required, unless joining
    // an existing organization that already has its providers
    if (this.selectedRole === "administrator" && !this.joinedOrganization) {
      if (this.hasInviteProviderFirstNameTarget && !this.inviteProviderFirstNameTarget.value.trim()) {
        errors.push({ target: this.inviteProviderFirstNameTarget, message: "Enter the provider's first name" })
      }
      if (this.hasInviteProviderLastNameTarget && !this.inviteProviderLastNameTarget.value.trim()) {
        errors.push({ target: this.inviteProviderLastNameTarget, message: "Enter the provider's last name" })
      }
      if (this.practiceType === "specialist" && this.hasInviteProviderSpecialtyTarget &&
          this.inviteProviderSpecialtyTarget.selectedOptions.length === 0) {
        errors.push({ target: this.inviteProviderSpecialtyTarget, message: "Select the provider's specialty" })
      }
      if (this.hasInviteProviderEmailTarget && !this.inviteProviderEmailTarget.value.trim()) {
        errors.push({ target: this.inviteProviderEmailTarget, message: "Enter the provider's email" })
      }
    }

    return errors
  }

  // Handle form submission
  submitForm(event) {
    this.clearAllErrors()
    const errors = this.collectErrors()

    if (errors.length > 0) {
      event.preventDefault()
      errors.forEach((error) => {
        if (error.address) {
          this.showAddressError(error.message)
        } else if (error.practiceType) {
          this.showPracticeTypeError(error.message)
        } else if (error.role) {
          this.showRoleError(error.message)
        } else {
          this.showFieldError(error.target, error.message)
        }
      })
      this.scrollToFirstError()
      return
    }

    // Valid — make sure unused address fields are disabled, then submit
    this.updateAddressFieldsDisabledState()
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Setting up...'
    }
  }

  // --- Error display helpers ---------------------------------------------

  showFieldError(target, message) {
    if (!target) return
    target.classList.add("is-invalid")
    const group = target.closest(".form-group") || target.parentElement
    if (!group) return
    let node = group.querySelector(".sign-up-error")
    if (!node) {
      node = document.createElement("div")
      node.className = "sign-up-error"
      group.appendChild(node)
    }
    node.textContent = message
  }

  clearFieldError(target) {
    if (!target || !target.classList) return
    target.classList.remove("is-invalid")
    const group = target.closest(".form-group") || target.parentElement
    const node = group && group.querySelector(".sign-up-error")
    if (node) node.remove()
  }

  showAddressError(message) {
    const section = (this.hasAddressLookupSectionTarget && !this.addressLookupSectionTarget.classList.contains("d-none"))
      ? this.addressLookupSectionTarget
      : this.manualAddressSectionTarget
    if (!section) return
    let node = section.querySelector(".sign-up-error.address-error")
    if (!node) {
      node = document.createElement("div")
      node.className = "sign-up-error address-error mb-2"
      section.appendChild(node)
    }
    node.textContent = message
  }

  clearAddressError() {
    this.element.querySelectorAll(".sign-up-error.address-error").forEach((n) => n.remove())
  }

  // The practice type: the joined organization's when joining (it is not
  // asked again), otherwise the segmented choice on the form.
  get practiceType() {
    if (this.joinedOrganization && this.hasJoinBannerTarget && this.joinBannerTarget.dataset.practiceType) {
      return this.joinBannerTarget.dataset.practiceType
    }
    const checked = this.hasPracticeTypeInputTarget && this.practiceTypeInputTargets.find((input) => input.checked)
    return checked ? checked.value : null
  }

  onPracticeTypeChange() {
    this.practiceTypeSectionTarget.classList.remove("is-invalid")
    this.element.querySelectorAll(".sign-up-error.practice-type-error").forEach((n) => n.remove())
    this.updateFieldsVisibility()
    this.applyPracticeType()
  }

  // The specialty selects don't offer General Dentistry. A general dentist
  // practice submits it through a hidden input for whichever provider the
  // form is describing (the user, or the provider an administrator adds), and
  // any specialty picked before switching is cleared so the two can't
  // contradict each other.
  applyPracticeType() {
    const isGeneralDentist = this.practiceType === "general_dentist"
    const isProvider = this.selectedRole === "provider"
    const isAdmin = this.selectedRole === "administrator"

    if (this.hasGeneralDentistryProviderInputTarget) {
      this.generalDentistryProviderInputTarget.disabled = !(isGeneralDentist && isProvider)
    }
    if (this.hasGeneralDentistryInviteInputTarget) {
      this.generalDentistryInviteInputTarget.disabled = !(isGeneralDentist && isAdmin && !this.joinedOrganization)
    }
    if (!isGeneralDentist) return

    ;[
      this.hasProviderSpecialtyTarget && this.providerSpecialtyTarget,
      this.hasInviteProviderSpecialtyTarget && this.inviteProviderSpecialtyTarget
    ].forEach((select) => {
      if (select && select.selectedOptions.length > 0) this.clearSelect(select)
    })
  }

  // Under the segments, the way the role error sits under the role options.
  showPracticeTypeError(message) {
    this.practiceTypeSectionTarget.classList.add("is-invalid")
    let node = this.element.querySelector(".sign-up-error.practice-type-error")
    if (!node) {
      node = document.createElement("div")
      node.className = "sign-up-error practice-type-error mb-2"
      const segments = this.practiceTypeSectionTarget.querySelector(".practice-type-segments")
      if (segments) segments.insertAdjacentElement("afterend", node)
    }
    node.textContent = message
  }

  showRoleError(message) {
    let node = this.element.querySelector(".sign-up-error.role-error")
    if (!node) {
      node = document.createElement("div")
      node.className = "sign-up-error role-error mb-2"
      const selection = this.hasRoleProviderTarget && this.roleProviderTarget.closest(".role-selection")
      if (selection) selection.insertAdjacentElement("afterend", node)
    }
    node.textContent = message
  }

  clearRoleError() {
    this.element.querySelectorAll(".sign-up-error.role-error").forEach((n) => n.remove())
  }

  clearAllErrors() {
    this.element.querySelectorAll(".is-invalid").forEach((el) => el.classList.remove("is-invalid"))
    this.element.querySelectorAll(".sign-up-error").forEach((el) => el.remove())
  }

  scrollToFirstError() {
    const first = this.element.querySelector(".is-invalid, .sign-up-error")
    if (first) first.scrollIntoView({ behavior: "smooth", block: "center" })
  }
}
